import React from 'react';
import { Box, Button, CircularProgress, Collapse, FormControlLabel, Switch, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import StatusChip from '../../../shared/components/StatusChip';
import InlineAlert from '../../../shared/components/InlineAlert';
import { BaselineLayer } from '../../../types/backend';
import {
  BaselineHealth, placementLine, summarizeBaselines, type BaselineSummary,
} from '../baselineStatus';
import BaselineStackDetails from './BaselineStackDetails';
import { useAccountBaselineStatus } from '../hooks/useAccountBaselineStatus';
import { useAccountBaselineActions } from '../hooks/useAccountBaselineActions';

export interface AccountBaselinesPanelProps {
  customerId: string;
  /** The 12-digit AWS account id, which is how the status response keys accounts. */
  awsAccountId: string;
  /**
   * Whether the caller may make the two decisions, switch an optional baseline
   * and run the account. All are admin level; below it the controls are not shown at all,
   * because escalating is the top bar's job, not this screen's.
   */
  allowActions: boolean;
}

interface BaselineRowProps {
  baseline: BaselineSummary;
  control?: React.ReactNode;
}

const BaselineRow: React.FC<BaselineRowProps> = ({ baseline, control }) => {
  const [open, setOpen] = React.useState(false);
  const detailsId = React.useId();
  const hasStacks = baseline.health !== BaselineHealth.OFF && baseline.stacks.length > 0;
  return (
  <Box
    role="group"
    aria-label={baseline.title}
    sx={{ border: 1, borderColor: 'divider', borderRadius: 1, px: 1.5, py: 1 }}
  >
    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
      <Typography variant="body2" sx={{ fontWeight: 500, flex: 1, minWidth: 0 }}>
        {baseline.title}
      </Typography>
      <StatusChip status={baseline.health} />
      {control}
    </Box>
    {hasStacks && (
      <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
        <span>Expected in</span>{' '}
        <Box component="span" sx={{ color: 'text.primary' }}>
          {placementLine(baseline.placements)}
        </Box>
      </Typography>
    )}
    {baseline.reason && (
      <Typography
        variant="caption"
        component="div"
        // Amber for a halt that needs a person, quiet for a wait that clears itself.
        color={baseline.health === BaselineHealth.WAITING_ON_DEPENDENCY ? 'text.secondary' : 'warning.main'}
        sx={{ mt: 0.25 }}
      >
        {baseline.reason}
      </Typography>
    )}
    {hasStacks && (
      <>
        <Button
          size="small"
          aria-label={`Details for ${baseline.title}`}
          aria-expanded={open}
          aria-controls={open ? detailsId : undefined}
          onClick={() => setOpen((o) => !o)}
          endIcon={<ExpandMoreIcon sx={{ transform: open ? 'rotate(180deg)' : 'none' }} />}
          sx={{ mt: 0.5, px: 0.5, minWidth: 0 }}
        >
          Details
        </Button>
        <Collapse in={open} unmountOnExit>
          <Box id={detailsId}>
            <BaselineStackDetails title={baseline.title} stacks={baseline.stacks} />
          </Box>
        </Collapse>
      </>
    )}
  </Box>
  );
};

/**
 * The account's baselines in their two layers: the foundation every enrolled
 * account carries, and the optional ones switched per account. One status per
 * baseline, rolled up from its stacks, with where the plan expects it; each
 * card folds out to its stack rows.
 */
const AccountBaselinesPanel: React.FC<AccountBaselinesPanelProps> = ({
  customerId, awsAccountId, allowActions,
}) => {
  const { accounts, error } = useAccountBaselineStatus(customerId);
  const account = accounts.find((a) => a.aws_account_id === awsAccountId);
  const {
    setOptional, runNow, setManaged, setBreakGlass, busy, error: actionError, clearError,
  } = useAccountBaselineActions(customerId, awsAccountId);

  // Nothing to say here on a failed read: AccountOnboardingPanel, mounted
  // under this panel on the same tab, reads the same query and shows its error.
  if (error || !account) return null;

  const baselines = summarizeBaselines(account);
  const foundation = baselines.filter((b) => b.layer === BaselineLayer.FOUNDATION);
  const optional = baselines.filter((b) => b.layer === BaselineLayer.OPTIONAL);
  const canRun = allowActions && Boolean(account.baseline_managed);

  return (
    <Box sx={{ px: { xs: 2, sm: 3 }, pt: 2, maxWidth: 860 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, flex: 1 }}>
          BASELINES
        </Typography>
        {busy && <CircularProgress size={14} />}
        {canRun && (
          <Button
            size="small"
            variant="outlined"
            disabled={busy}
            onClick={runNow}
          >
            Run now
          </Button>
        )}
      </Box>

      {actionError && (
        <InlineAlert severity="error" onClose={clearError} sx={{ mt: 1 }}>
          {actionError}
        </InlineAlert>
      )}

      {allowActions && (
        <Box component="section" aria-label="Decisions" sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
          {/*
            Active says OpsPilot manages the account at all; this says OpsPilot
            installs and keeps its baseline stacks. Moved one account at a time
            during the migration off Service Catalog.
          */}
          <FormControlLabel
            control={(
              <Switch
                disabled={busy}
                checked={Boolean(account.baseline_managed)}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setManaged(e.target.checked)}
              />
            )}
            label={(
              <Box>
                <Typography variant="body2">OpsPilot installs the baselines</Typography>
                <Typography variant="caption" color="text.secondary">
                  Turn on to move this account onto direct CloudFormation. Until then its baselines stay with
                  Service Catalog. It starts changing this account within the hour.
                </Typography>
              </Box>
            )}
          />
          <FormControlLabel
            control={(
              <Switch
                disabled={busy}
                checked={Boolean(account.break_glass_required)}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setBreakGlass(e.target.checked)}
              />
            )}
            label={(
              <Box>
                <Typography variant="body2">Customer controls our admin access</Typography>
                <Typography variant="caption" color="text.secondary">
                  We stay read-only here. The customer runs break-glass in their own tenant to let us in, and
                  closes it afterwards. For customers who may not grant us standing admin.
                </Typography>
              </Box>
            )}
          />
        </Box>
      )}

      <Box component="section" aria-label="Foundation" sx={{ mt: 1 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>
          Foundation
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          {foundation.map((b) => <BaselineRow key={b.name} baseline={b} />)}
        </Box>
      </Box>

      <Box component="section" aria-label="Optional" sx={{ mt: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>
          Optional
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          {optional.map((b) => (
            <BaselineRow
              key={b.name}
              baseline={b}
              control={allowActions ? (
                <FormControlLabel
                  sx={{ mr: 0 }}
                  label={<Typography variant="caption">Install {b.title}</Typography>}
                  control={(
                    <Switch
                      size="small"
                      disabled={busy}
                      checked={b.enabled}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setOptional(b.name, e.target.checked)}
                    />
                  )}
                />
              ) : undefined}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default AccountBaselinesPanel;
